/**
 * Authentication Modal
 *
 * A modal component for user sign in.
 * Supports Google OAuth, Microsoft OAuth, and email/password authentication.
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { FcGoogle } from 'react-icons/fc';
import { FaMicrosoft } from 'react-icons/fa';

// Type cast for React 19 compatibility
const GoogleIcon = FcGoogle as React.ComponentType<{ className?: string }>;
const MicrosoftIcon = FaMicrosoft as React.ComponentType<{
  className?: string;
}>;

export type AuthView = 'signIn' | 'signUp' | 'forgotPassword' | 'checkEmail';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  returnUrl?: string;
  /** When the modal opens, start on this screen (e.g. sign-up from a quota prompt). */
  initialView?: AuthView;
}

const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  returnUrl,
  initialView = 'signIn',
}) => {
  const {
    signIn,
    signUp,
    signInWithGoogle,
    signInWithMicrosoft,
    resetPassword,
  } = useAuth();
  const [loadingProvider, setLoadingProvider] = useState<
    'google' | 'microsoft' | 'email' | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<AuthView>(initialView);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setAgreedToTerms(false);
    setError(null);
    setLoadingProvider(null);
  };

  useEffect(() => {
    if (!isOpen) return;
    resetForm();
    setView(initialView);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset when modal opens / initial target changes
  }, [isOpen, initialView]);

  const switchView = (newView: AuthView) => {
    resetForm();
    setView(newView);
  };

  const requireSignupAgreement = (): boolean => {
    if (view === 'signUp' && !agreedToTerms) {
      setError(
        'Please agree to the Terms and Privacy Policy to create an account.'
      );
      return false;
    }
    return true;
  };

  const handleGoogleSignIn = async () => {
    if (!requireSignupAgreement()) return;
    setLoadingProvider('google');
    setError(null);
    const { error } = await signInWithGoogle(returnUrl);
    if (error) {
      setError(error);
      setLoadingProvider(null);
    }
  };

  const handleMicrosoftSignIn = async () => {
    if (!requireSignupAgreement()) return;
    setLoadingProvider('microsoft');
    setError(null);
    const { error } = await signInWithMicrosoft(returnUrl);
    if (error) {
      setError(error);
      setLoadingProvider(null);
    }
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setLoadingProvider('email');
    setError(null);
    const { error } = await signIn(email, password);
    if (error) {
      setError(error);
      setLoadingProvider(null);
    } else {
      resetForm();
      onClose();
      window.location.href = '/app/dashboard';
    }
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!requireSignupAgreement()) return;
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoadingProvider('email');
    setError(null);
    const { error } = await signUp(email, password);
    if (error) {
      setError(error);
      setLoadingProvider(null);
    } else {
      setLoadingProvider(null);
      setView('checkEmail');
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address.');
      return;
    }
    setLoadingProvider('email');
    setError(null);
    const { error } = await resetPassword(email);
    if (error) {
      setError(error);
      setLoadingProvider(null);
    } else {
      setLoadingProvider(null);
      setView('checkEmail');
    }
  };

  if (!isOpen) return null;

  const renderCheckEmail = () => (
    <div className='text-center py-4'>
      <div className='w-16 h-16 mx-auto mb-4 rounded-full bg-primary-50 flex items-center justify-center'>
        <svg
          className='w-8 h-8 text-primary-600'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
          />
        </svg>
      </div>
      <h3 className='text-xl font-semibold text-gray-900 mb-2'>
        Check your email
      </h3>
      <p className='text-gray-600 text-sm mb-6'>
        We sent a link to <strong>{email}</strong>. Click the link in the email
        to{' '}
        {view === 'checkEmail' && password
          ? 'verify your account'
          : 'reset your password'}
        .
      </p>
      <button
        onClick={() => switchView('signIn')}
        className='text-sm text-primary-600 hover:text-primary-700 font-medium'
      >
        Back to sign in
      </button>
    </div>
  );

  const renderForgotPassword = () => (
    <form onSubmit={handleForgotPassword} className='space-y-4'>
      <div>
        <h3 className='text-xl font-semibold text-gray-900 mb-1'>
          Reset your password
        </h3>
        <p className='text-sm text-gray-600'>
          Enter your email and we'll send you a reset link.
        </p>
      </div>
      <div>
        <label
          htmlFor='reset-email'
          className='block text-sm font-medium text-gray-700 mb-1'
        >
          Email
        </label>
        <input
          id='reset-email'
          type='email'
          value={email}
          onChange={e => setEmail(e.target.value)}
          className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm'
          placeholder='you@example.com'
          autoComplete='email'
          required
        />
      </div>
      <Button
        type='submit'
        disabled={!!loadingProvider}
        className='w-full'
        size='lg'
      >
        {loadingProvider === 'email' ? 'Sending...' : 'Send reset link'}
      </Button>
      <button
        type='button'
        onClick={() => switchView('signIn')}
        className='w-full text-sm text-gray-500 hover:text-gray-700'
      >
        Back to sign in
      </button>
    </form>
  );

  const renderEmailForm = () => {
    const isSignUp = view === 'signUp';

    return (
      <form
        onSubmit={isSignUp ? handleEmailSignUp : handleEmailSignIn}
        className='space-y-3'
      >
        <div>
          <label
            htmlFor='auth-email'
            className='block text-sm font-medium text-gray-700 mb-1'
          >
            Email
          </label>
          <input
            id='auth-email'
            type='email'
            value={email}
            onChange={e => setEmail(e.target.value)}
            className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm'
            placeholder='you@example.com'
            autoComplete='email'
            required
          />
        </div>
        <div>
          <label
            htmlFor='auth-password'
            className='block text-sm font-medium text-gray-700 mb-1'
          >
            Password
          </label>
          <input
            id='auth-password'
            type='password'
            value={password}
            onChange={e => setPassword(e.target.value)}
            className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm'
            placeholder={isSignUp ? 'At least 6 characters' : 'Your password'}
            autoComplete={isSignUp ? 'new-password' : 'current-password'}
            required
          />
        </div>
        {isSignUp && (
          <div>
            <label
              htmlFor='auth-confirm-password'
              className='block text-sm font-medium text-gray-700 mb-1'
            >
              Confirm password
            </label>
            <input
              id='auth-confirm-password'
              type='password'
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm'
              placeholder='Confirm your password'
              autoComplete='new-password'
              required
            />
          </div>
        )}
        {!isSignUp && (
          <div className='text-right'>
            <button
              type='button'
              onClick={() => switchView('forgotPassword')}
              className='text-xs text-primary-600 hover:text-primary-700'
            >
              Forgot password?
            </button>
          </div>
        )}
        <Button
          type='submit'
          disabled={!!loadingProvider}
          className='w-full'
          size='lg'
        >
          {loadingProvider === 'email'
            ? isSignUp
              ? 'Creating account...'
              : 'Signing in...'
            : isSignUp
              ? 'Create account'
              : 'Sign in'}
        </Button>
      </form>
    );
  };

  const modalContent = (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm'>
      <div className='relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 animate-in fade-in zoom-in duration-200'>
        {/* Close button */}
        <button
          onClick={() => {
            resetForm();
            setView('signIn');
            onClose();
          }}
          className='absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors'
          aria-label='Close'
        >
          <svg
            className='w-6 h-6'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M6 18L18 6M6 6l12 12'
            />
          </svg>
        </button>

        {view === 'checkEmail' ? (
          renderCheckEmail()
        ) : view === 'forgotPassword' ? (
          <>
            {/* Header */}
            <div className='mb-6' />
            {/* Error message */}
            {error && (
              <div className='mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm'>
                {error}
              </div>
            )}
            {renderForgotPassword()}
          </>
        ) : (
          <>
            {/* Header */}
            <div className='text-center mb-6'>
              <h2 className='text-3xl font-bold text-gray-900 mb-3'>
                Welcome to Layerre
              </h2>
              <p className='text-gray-700 text-base mb-4'>
                The API for automating designs
              </p>
            </div>

            {/* Features list */}
            <div className='mb-6 space-y-2 text-sm text-gray-600'>
              <div className='flex items-start gap-2'>
                <svg
                  className='w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M5 13l4 4L19 7'
                  />
                </svg>
                <span>Create programmable design templates</span>
              </div>
              <div className='flex items-start gap-2'>
                <svg
                  className='w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M5 13l4 4L19 7'
                  />
                </svg>
                <span>Generate design variations via API</span>
              </div>
              <div className='flex items-start gap-2'>
                <svg
                  className='w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M5 13l4 4L19 7'
                  />
                </svg>
                <span>Save and manage all your templates in one place</span>
              </div>
            </div>

            {view === 'signUp' && (
              <label className='flex items-start gap-2 mb-4 text-xs text-gray-600 cursor-pointer'>
                <input
                  type='checkbox'
                  checked={agreedToTerms}
                  onChange={e => {
                    setAgreedToTerms(e.target.checked);
                    if (error) setError(null);
                  }}
                  className='mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500'
                />
                <span>
                  I agree to the{' '}
                  <a
                    href='/terms/'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-blue-500 hover:text-blue-600 underline'
                    onClick={e => e.stopPropagation()}
                  >
                    Terms
                  </a>{' '}
                  and{' '}
                  <a
                    href='/privacy/'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-blue-500 hover:text-blue-600 underline'
                    onClick={e => e.stopPropagation()}
                  >
                    Privacy Policy
                  </a>
                  .
                </span>
              </label>
            )}

            {/* OAuth Sign In Buttons */}
            <div className='space-y-3 mb-4'>
              <Button
                type='button'
                onClick={handleGoogleSignIn}
                disabled={!!loadingProvider}
                className='w-full bg-white text-gray-700 border-2 border-gray-300 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed'
                size='lg'
              >
                <GoogleIcon className='w-5 h-5 mr-2' />
                Continue with Google
              </Button>

              <Button
                type='button'
                onClick={handleMicrosoftSignIn}
                disabled={!!loadingProvider}
                className='w-full bg-white text-gray-700 border-2 border-gray-300 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed'
                size='lg'
              >
                <MicrosoftIcon className='w-5 h-5 mr-2 text-[#00A4EF]' />
                Continue with Microsoft
              </Button>
            </div>

            {/* Divider */}
            <div className='relative my-5'>
              <div className='absolute inset-0 flex items-center'>
                <div className='w-full border-t border-gray-200' />
              </div>
              <div className='relative flex justify-center text-xs'>
                <span className='bg-white px-3 text-gray-400'>
                  or continue with email
                </span>
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className='mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm'>
                {error}
              </div>
            )}

            {/* Email/Password Form */}
            {renderEmailForm()}

            {/* Toggle Sign In / Sign Up */}
            <p className='text-center text-sm text-gray-500 mt-4'>
              {view === 'signUp' ? (
                <>
                  Already have an account?{' '}
                  <button
                    type='button'
                    onClick={() => switchView('signIn')}
                    className='text-primary-600 hover:text-primary-700 font-medium'
                  >
                    Sign in
                  </button>
                </>
              ) : (
                <>
                  Don&apos;t have an account?{' '}
                  <button
                    type='button'
                    onClick={() => switchView('signUp')}
                    className='text-primary-600 hover:text-primary-700 font-medium'
                  >
                    Sign up
                  </button>
                </>
              )}
            </p>

            {view === 'signIn' && (
              <p className='text-center text-xs text-gray-500 mt-4'>
                By signing in, you agree to our{' '}
                <a
                  href='/terms/'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-blue-500 hover:text-blue-600 underline'
                >
                  Terms
                </a>{' '}
                and{' '}
                <a
                  href='/privacy/'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-blue-500 hover:text-blue-600 underline'
                >
                  Privacy Policy
                </a>
                .
              </p>
            )}
          </>
        )}

        {/* Full-modal loading overlay */}
        {loadingProvider && loadingProvider !== 'email' && (
          <div className='absolute inset-0 rounded-2xl bg-white/95 flex flex-col items-center justify-center z-10'>
            <div className='animate-spin w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full mb-3' />
            <p className='text-gray-700 font-medium'>
              {loadingProvider === 'google'
                ? 'Signing in with Google...'
                : 'Signing in with Microsoft...'}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AuthModal;
